import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { up } from "@/components/identity/Identity";

const addrText = (a) => (a ? [a.unit, a.address_line1, a.address_line2, a.street, a.area, a.landmark].filter(Boolean).join("\n") : "");

function ContactLine({ label, c }) {
  if (!c || !(c.name || c.number)) return null;
  return <div><span className="text-slate-500">{label}: </span>{c.name && <b className="uppercase">{up(c.name)}</b>}{c.relationship && <span className="text-slate-500"> ({c.relationship})</span>}{c.name && c.number ? " · " : ""}{c.number}</div>;
}

/** Employee-proposed delivery changes (self-confirm link) awaiting authorised review. Accepting applies them through the normal audited delivery edit. */
export function EmployeeProposals({ requestId, onApplied }) {
  const [d, setD] = useState({ items: [], can_review: false });
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/requests/${requestId}/delivery/proposals`).then((r) => setD(r.data)).catch(() => {}), [requestId]);
  useEffect(() => { load(); }, [load]);
  const open = d.items.filter((p) => p.status === "open");
  if (!d.items.length) return null;
  const decide = async (p, accept) => {
    setBusy(p.id);
    try { await api.post(`/requests/${requestId}/delivery/proposals/${p.id}/decide`, { accept }); toast.success(accept ? "Employee change accepted and applied" : "Employee change rejected"); await load(); onApplied?.(); }
    catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : det?.message || "Could not record the decision"); }
    finally { setBusy(""); }
  };
  return (
    <div className="st-panel p-3" data-testid="employee-proposals">
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">Employee-proposed changes {open.length ? `· ${open.length} awaiting review` : ""}</div>
      <div className="mt-2 space-y-2">
        {d.items.map((p) => (
          <div key={p.id} className="rounded-md border border-slate-200 p-2 text-[12px]" data-testid={`proposal-${p.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-[11px] text-slate-500">{(p.created_at || "").slice(0, 16).replace("T", " ")} · {p.employee_email}</span>
              <span className={`rounded px-1.5 text-[10px] font-bold ${p.status === "open" ? "bg-amber-50 text-amber-800" : p.status === "accepted" ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`proposal-status-${p.id}`}>{p.status.toUpperCase()}</span></div>
            {p.proposed_address && <div className="mt-1"><span className="text-slate-500">Address: </span><span className="whitespace-pre-line">{addrText(p.proposed_address)}</span>
              <div className="font-bold uppercase underline underline-offset-2">{[up(p.proposed_address.city), up(p.proposed_address.postal_code)].filter(Boolean).join(" – ")}</div>
              <div className="font-bold uppercase underline underline-offset-2">{[up(p.proposed_address.state), up(p.proposed_address.country)].filter(Boolean).join(", ")}</div></div>}
            <ContactLine label="Primary contact" c={p.proposed_primary_contact} />
            <ContactLine label="Secondary contact" c={p.proposed_secondary_contact} />
            {p.note && <div className="text-slate-500">Note: {p.note}</div>}
            {p.status === "open" && d.can_review && <div className="mt-2 flex gap-2">
              <Button size="sm" className="h-7 text-[11px]" disabled={!!busy} onClick={() => decide(p, true)} data-testid={`proposal-accept-${p.id}`}>Accept & apply</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!busy} onClick={() => decide(p, false)} data-testid={`proposal-reject-${p.id}`}>Reject</Button>
            </div>}
            {p.status !== "open" && p.decided_by && <div className="mt-1 text-[10.5px] text-slate-500">Decided by {p.decided_by}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
